/**
 * DOM automation actions for demo mode.
 * These execute on the LIVE customer page (no iframe).
 */

import { animateCursorTo, showClickRipple, hideCursor } from './cursor.js';

// ─── Utilities ─────────────────────────────────────────────────────────────

function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
}

/**
 * Hints used by {@link resolveElement} when the primary CSS selector
 * doesn't match. The server populates these from the PDP element metadata
 * (label, type, highlight_text) — see response-parser.ts.
 */
export interface SelectorFallback {
  /** Human-readable label, e.g. "Submit", "Email address". */
  label?: string;
  /** PDP element kind — button | input | link | tab | … */
  type?: string;
  /** Visible text snippet from the PDP that should appear inside the element. */
  text?: string;
}

/**
 * Per-action verification report produced after an action dispatches. Distinct
 * from "did the lookup succeed" (that's the throw/no-throw of resolveElement)
 * — this captures whether the action *had an effect*. Without it, a synthetic
 * `.click()` on a React-controlled input or a `<select>.value =` on a
 * controlled select reports success but the visible state never changes; the
 * agent then narrates "I clicked it" while the visitor sees nothing happen.
 *
 * `status` semantics:
 *  - 'verified'           → action observed to have taken effect (DOM mutated,
 *                           value matched what we typed, route changed, etc.)
 *  - 'executed_unverified'→ action dispatched cleanly but no observable
 *                           signal of effect was found within the settle
 *                           window. Common on hover-only triggers and on
 *                           pages that suppress `input` events.
 *  - 'failed'             → action dispatched but a verification check
 *                           explicitly contradicted success (typed value
 *                           reset, native select rejected the option, etc.).
 *                           The action did NOT achieve its goal.
 *  - 'no_op'              → the action's declared post-condition was already
 *                           satisfied at dispatch time and the action was
 *                           short-circuited (server-side) or detected as
 *                           redundant after settle (iframe-side). The agent
 *                           should narrate from the existing state instead
 *                           of re-triggering it.
 */
export interface VerificationResult {
  status: 'verified' | 'executed_unverified' | 'failed' | 'no_op';
  /** Concrete observations that fed the status decision. */
  observed: {
    domMutated?: boolean;
    routeChanged?: boolean;
    activeElementChanged?: boolean;
    valueAfter?: string;
    valueExpected?: string;
    /**
     * `elementFromPoint(x, y)` was something other than the click target or
     * one of its descendants — usually a fixed header, tooltip, or modal
     * overlay sliding over the button while we were animating the cursor.
     * The click event still bubbles so the page may visibly respond, but
     * the response will be the overlay's, not the target's.
     */
    clickPointMismatch?: boolean;
    /** When clickPointMismatch is true, what was actually under the point. */
    clickPointActualTag?: string;
  };
  /** Specific failure(s). Present only when `status === 'failed'`. */
  errors?: Array<{
    code:
      | 'value_mismatch'        // typed/selected value did not stick
      | 'no_observable_effect'  // click landed but page didn't move
      | 'animation_in_progress' // skipped verification because mid-animation
      | 'timeout'               // settle window expired before signal
      | 'click_point_blocked';  // overlay covered the target at click time
    message: string;
  }>;
  /** Wall-clock action latency in milliseconds (dispatch + settle). */
  latencyMs: number;
  /** Resolver strategy that found the element (drift signal). */
  resolverStrategy: 'direct' | 'aria' | 'text' | 'tail';
  /**
   * Result of evaluating the declared post-condition for this action,
   * if one was supplied via `expects`. Distinct from `status` — `status`
   * is "did *something* change", `postCondition.state` is "did the
   * intended outcome obtain". Lets the agent (and the action-trace ring
   * buffer on the server) detect toggle-close and redundant-action bugs
   * that pure DOM-mutation verification can't catch.
   */
  postCondition?: {
    state: 'satisfied' | 'unsatisfied' | 'unknown';
    cond: import('./post-condition.js').PostCondition;
  };
}

/** Common settle window for post-dispatch DOM/route/value checks. */
const VERIFY_SETTLE_MS = 250;

/**
 * D31 — adaptive settle. Wait for the DOM to go quiet (no mutations for
 * `quietMs` consecutive ms) up to `maxMs`. Falls back to {@link
 * VERIFY_SETTLE_MS} when the page is already quiet at call time. Used by
 * actions that may trigger React 18 Suspense / framer-motion / route-loader
 * cascades whose settled state arrives well past the 250ms fixed window.
 *
 * Returns the actual wait time so callers can attach it to verification
 * latency for diagnostics.
 */
async function waitForDomQuiet(quietMs = 120, maxMs = 1500): Promise<number> {
  const t0 = performance.now();
  let lastMutation = t0;
  const observer = new MutationObserver(() => {
    lastMutation = performance.now();
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: false, // text-content churn isn't usually load-meaningful
  });
  try {
    while (true) {
      const now = performance.now();
      const sinceMutation = now - lastMutation;
      const sinceStart = now - t0;
      if (sinceMutation >= quietMs) return Math.round(sinceStart);
      if (sinceStart >= maxMs) return Math.round(sinceStart);
      // Sleep proportional to how close we are to the quiet target so we
      // don't overshoot — but bounded so a high-churn page doesn't busy-poll.
      const wait = Math.min(60, Math.max(20, quietMs - sinceMutation));
      await new Promise((r) => setTimeout(r, wait));
    }
  } finally {
    observer.disconnect();
  }
}

/**
 * Walk every reachable open shadow root. Mirrors element-mapper's helper —
 * duplicated to keep actions.ts dependency-free of capture code (the agent
 * bundle is shipped to visitors and we keep its surface area tight).
 */
function collectShadowRoots(): ShadowRoot[] {
  const out: ShadowRoot[] = [];
  const stack: Array<Document | ShadowRoot> = [document];
  while (stack.length > 0) {
    const root = stack.pop()!;
    const walker = document.createTreeWalker(
      root === document ? document.documentElement : (root as ShadowRoot),
      NodeFilter.SHOW_ELEMENT,
    );
    let node = walker.nextNode();
    while (node) {
      const sr = (node as Element).shadowRoot;
      if (sr) {
        out.push(sr);
        stack.push(sr);
      }
      node = walker.nextNode();
    }
  }
  return out;
}

/**
 * Cluster C item 25: detect when a target lives under a closed shadow root.
 * `attachShadow({ mode: 'closed' })` is opaque — there's no way to query
 * inside, so we can't click, type, or even see the element. Knowing this
 * lets the action helper return `unreachable_dom` so the agent can pivot
 * to keyboard-driven interaction instead of looping on a phantom selector.
 *
 * Heuristic: walk the ancestry of the visible chain looking for an element
 * whose `.shadowRoot` is null but which has a likely-closed-shadow signature
 * (custom element with `-` in tag name, `attachShadow` referenced via the
 * known polyfill flag we set, or no children but visible content).
 */
export function hasClosedShadowAncestor(el: Element): boolean {
  let cursor: Element | null = el;
  let depth = 0;
  while (cursor && depth < 30) {
    const tag = cursor.tagName.toLowerCase();
    // Custom element with no shadowRoot accessor + no children = strong
    // signature of closed-shadow-mode (the renderer is invisible to us).
    if (tag.includes('-') && cursor.shadowRoot === null && cursor.children.length === 0) {
      return true;
    }
    cursor = cursor.parentElement;
    depth++;
  }
  return false;
}

/**
 * Shadow-DOM-piercing querySelector. Returns the first match across the
 * document and every reachable open shadow root, in document order. Without
 * this, the agent can SEE elements inside shadow DOM via the snapshot but
 * cannot act on them — `document.querySelector` doesn't pierce. P1.
 */
function querySelectorDeep(selector: string): Element | null {
  try {
    const direct = document.querySelector(selector);
    if (direct) return direct;
  } catch { /* malformed selector — skip */ }
  for (const sr of collectShadowRoots()) {
    try {
      const inSr = sr.querySelector(selector);
      if (inSr) return inSr;
    } catch { /* skip */ }
  }
  return null;
}

/**
 * Shadow-DOM-piercing querySelectorAll. Same semantics as the deep
 * querySelector, but returns every match.
 */
function querySelectorAllDeep(selector: string): Element[] {
  const out: Element[] = [];
  try {
    document.querySelectorAll(selector).forEach((el) => out.push(el));
  } catch { /* skip */ }
  for (const sr of collectShadowRoots()) {
    try {
      sr.querySelectorAll(selector).forEach((el) => out.push(el));
    } catch { /* skip */ }
  }
  return out;
}

/**
 * Capture pre-action state needed for after-the-fact verification. Returns a
 * thunk that, when called post-dispatch (after VERIFY_SETTLE_MS), produces an
 * `observed` snapshot diff against the moment of capture.
 *
 * Uses a one-shot MutationObserver on the document so we can tell whether
 * the action caused ANY DOM change. We don't try to attribute the mutation
 * to the action — pages have their own background mutations and we'd lose
 * accuracy more than we'd gain. A bare "did anything change" signal is the
 * right proxy for "did this click do something" 95% of the time.
 */
function snapshotPreActionState(target: Element): () => VerificationResult['observed'] {
  const startUrl = location.href;
  const startActive = document.activeElement;
  let mutated = false;
  const observer = new MutationObserver(() => {
    mutated = true;
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });

  return () => {
    observer.disconnect();
    return {
      domMutated: mutated,
      routeChanged: location.href !== startUrl,
      activeElementChanged: document.activeElement !== startActive,
    };
  };
}

/**
 * Outcome of a self-healing element lookup. `recoveredSelector` is non-null
 * iff the primary selector missed and a fallback strategy succeeded — the
 * iframe-agent dispatcher reports this back to the platform so PDP authors
 * can see which selectors are stale (powers the #42 auto-correct flow).
 */
export interface ResolvedElement {
  element: Element;
  /** A more durable selector for the recovered element, when fallback fired. */
  recoveredSelector?: string;
  /** Which strategy produced the match — useful in logs. */
  strategy: 'direct' | 'aria' | 'text' | 'tail';
}

/**
 * Self-healing element lookup. Tries strategies in order:
 *   1. `direct`   — `document.querySelector(selector)` exactly as given.
 *   2. `aria`     — when fallback.label is present, try `[aria-label]` /
 *                   `[aria-labelledby]` / role + accessible-name matches.
 *   3. `text`     — iterate likely interactive tags (button, a, [role=button],
 *                   [role=link], etc.) and return the one whose visible text
 *                   trims to fallback.label / fallback.text.
 *   4. `tail`     — strip the leftmost compound parts of the selector and
 *                   retry — handles brittle "5-deep div soup" selectors that
 *                   break when the upstream layout shifts.
 *
 * Visible-only: invisible, display:none, and `aria-hidden` matches are
 * skipped. Partial matches that exceed `MAX_TEXT_MATCH_LEN` are ignored to
 * avoid grabbing a giant container div whose text happens to contain "Submit".
 */
// Cluster C item 22: dropped from 80 → 30. At 80, single-button labels like
// "Save changes and continue to billing setup" (62 chars) sat under the cap
// but matched parent containers whose text included the same phrase. 30 chars
// covers every realistic CTA copy ("Get started", "Add team member", "Apply")
// without grabbing wrappers.
const MAX_TEXT_MATCH_LEN = 30;
const TEXT_TARGET_TAGS = [
  'button',
  'a',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  'input[type="submit"]',
  'input[type="button"]',
];

export function resolveElement(
  selector: string,
  fallback?: SelectorFallback,
): ResolvedElement | null {
  // 1. Direct — pierce shadow roots so design-system components are
  // reachable. Without this, snapshots surface shadow-rooted elements but
  // resolveElement can't find them and clicks always fall through to the
  // fallback strategies (or fail entirely). P1.
  const direct = selector ? querySelectorDeep(selector) : null;
  if (direct && isVisible(direct)) {
    return { element: direct, strategy: 'direct' };
  }

  // 1b. Form-input fallback for bare-word selectors. PDPs sometimes
  // store credential keys as `email` / `password` / `username` instead
  // of valid CSS selectors like `[name='email']`. Without this fallback
  // the agent silently submits an empty form. Trigger only when the
  // selector is a single alphanumeric/underscore/dash word — anything
  // with `.` `#` `[` `:` `>` or whitespace was already a real selector
  // and should NOT fall through here.
  if (selector && /^[a-zA-Z][a-zA-Z0-9_-]*$/.test(selector)) {
    const word = selector;
    const wordEsc = cssEscapeAttr(word);
    const candidates = [
      // Exact name match — overwhelmingly the right answer for sign-in
      // forms. Same for textarea and select for completeness.
      `input[name="${wordEsc}"]`,
      `textarea[name="${wordEsc}"]`,
      `select[name="${wordEsc}"]`,
      // type="email" / type="password" / type="tel" — modern HTML5 forms.
      `input[type="${wordEsc}"]`,
      // autocomplete tokens — set by sites that respect form managers.
      `input[autocomplete="${wordEsc}"]`,
      // Common id conventions.
      `#${wordEsc}`,
      `#${wordEsc}-input`,
      `#${wordEsc}_input`,
      // Last-resort: placeholder substring (case-insensitive).
      `input[placeholder*="${wordEsc}" i]`,
    ];
    for (const sel of candidates) {
      const el = querySelectorDeep(sel);
      if (el && isVisible(el)) {
        // Tag the strategy as 'aria' for now — same audit shape; the
        // dashboard groups recoveries by recoveredSelector regardless.
        return { element: el, recoveredSelector: sel, strategy: 'aria' };
      }
    }
  }

  if (!fallback) return null;
  const label = (fallback.label ?? '').trim();
  const text = (fallback.text ?? '').trim();
  const target = (label || text).slice(0, MAX_TEXT_MATCH_LEN);

  // 2. ARIA / role-based
  if (label) {
    const escaped = cssEscapeAttr(label);
    const ariaCandidates = [
      `[aria-label="${escaped}"]`,
      `[title="${escaped}"]`,
    ];
    if (fallback.type === 'button') ariaCandidates.unshift(`button[aria-label="${escaped}"]`);
    for (const sel of ariaCandidates) {
      const el = querySelectorDeep(sel);
      if (el && isVisible(el)) {
        return { element: el, recoveredSelector: sel, strategy: 'aria' };
      }
    }
  }

  // 3. Text-content match across likely interactive elements.
  // Cluster C item 24: when 2+ candidates match the same text exactly, refuse
  // and return null instead of silently picking the first. Two "OK" buttons
  // on the same screen produced wrong-button clicks too often. The agent's
  // recovery loop will fall back to a more specific cue (verb + role + form
  // group) on the next turn.
  if (target) {
    const lowerTarget = target.toLowerCase();
    const matches: Array<{ el: Element; tagSelector: string }> = [];
    for (const tagSelector of TEXT_TARGET_TAGS) {
      const candidates = querySelectorAllDeep(tagSelector);
      for (const el of candidates) {
        if (!isVisible(el)) continue;
        const t = (el as HTMLElement).innerText ?? el.textContent ?? '';
        const trimmed = t.trim();
        if (!trimmed || trimmed.length > MAX_TEXT_MATCH_LEN) continue;
        if (trimmed.toLowerCase() === lowerTarget) {
          matches.push({ el, tagSelector });
          if (matches.length > 1) break;
        }
      }
      if (matches.length > 1) break;
    }
    if (matches.length === 1) {
      const { el, tagSelector } = matches[0];
      return {
        element: el,
        recoveredSelector: buildStableSelectorFor(el, tagSelector),
        strategy: 'text',
      };
    }
    // matches.length === 0 → fall through. matches.length > 1 → refuse;
    // ambiguity is worse than failure here.
  }

  // 4. Tail-strip the original selector. Cluster C item 21: this used to be
  // a permissive "first match wins" loop, which produced wrong-element clicks
  // more often than right ones. Now we only accept a tail match when it is
  // ALSO the only match in the document AND its visible role/tag matches
  // fallback.type. Anything ambiguous returns null and lets the agent retry
  // with a better cue.
  if (selector.includes(' ')) {
    const parts = selector.split(/\s+/).filter(Boolean);
    for (let i = 1; i < parts.length; i++) {
      const tail = parts.slice(i).join(' ');
      const all = querySelectorAllDeep(tail).filter(isVisible);
      if (all.length !== 1) continue;
      const el = all[0];
      if (fallback?.type) {
        const role = (el.getAttribute('role') ?? '').toLowerCase();
        const tag = el.tagName.toLowerCase();
        if (fallback.type === 'button' && tag !== 'button' && role !== 'button') continue;
        if (fallback.type === 'link' && tag !== 'a' && role !== 'link') continue;
      }
      return { element: el, recoveredSelector: tail, strategy: 'tail' };
    }
  }

  return null;
}

function isVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement) && !(el instanceof SVGElement)) return false;
  if ((el as HTMLElement).hidden) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  // Cluster C item 29: `inert` blocks all interaction including focus, so
  // an element inside an inert container is effectively invisible to the
  // agent regardless of pixels-on-screen. Walk a small ancestry to catch it.
  let cursor: Element | null = el;
  let depth = 0;
  while (cursor && depth < 20) {
    if (cursor.hasAttribute && cursor.hasAttribute('inert')) return false;
    cursor = cursor.parentElement;
    depth++;
  }
  // offsetParent === null for display:none / detached. SVG elements don't
  // have offsetParent — fall back to bounding rect for those.
  if (el instanceof HTMLElement && el.offsetParent === null && el.tagName !== 'BODY') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Cluster C item 29: full disabled detection used by action helpers before
 * dispatching a click. Distinct from {@link isVisible} — the element exists
 * and renders, but interacting with it is a no-op. Action helpers that
 * surface a structured error code use this to set 'element_disabled'
 * instead of 'unknown'.
 */
export function isInteractionDisabled(el: Element): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.hasAttribute('disabled')) return true;
  if (el.getAttribute('aria-disabled') === 'true') return true;
  if (el.getAttribute('aria-busy') === 'true') return true;
  // Walk ancestors for fieldset[disabled], aria-disabled containers, and
  // pointer-events:none chains.
  let cursor: HTMLElement | null = el;
  let depth = 0;
  while (cursor && depth < 25) {
    if (cursor.tagName === 'FIELDSET' && cursor.hasAttribute('disabled')) return true;
    if (cursor.getAttribute && cursor.getAttribute('aria-disabled') === 'true' && cursor !== el) return true;
    const styles = window.getComputedStyle(cursor);
    if (styles.pointerEvents === 'none') return true;
    cursor = cursor.parentElement;
    depth++;
  }
  return false;
}

/** Escape a string for safe inclusion in a CSS attribute selector value. */
function cssEscapeAttr(value: string): string {
  return value.replace(/(["\\\\])/g, '\\$1');
}

/**
 * Best-effort stable selector for a recovered element. We prefer
 * `data-testid` / `id` when present, falling back to the tag-selector that
 * found it. NOT meant to be a perfect selector generator — it's a
 * suggestion that the platform layer surfaces back to the PDP author.
 */
function buildStableSelectorFor(el: Element, tagSelector: string): string {
  const testId = el.getAttribute('data-testid');
  if (testId) return `[data-testid="${cssEscapeAttr(testId)}"]`;
  const id = el.getAttribute('id');
  if (id && /^[A-Za-z][\w-]*$/.test(id)) return `#${id}`;
  return tagSelector;
}

/**
 * Wait for any in-flight CSS transitions / animations on `el` to finish, up
 * to `maxMs`. Resolves on first `transitionend`/`animationend` *if any are
 * pending*, or immediately when none are. Without this, fast clicks on a
 * still-animating dropdown trigger or a ripple-effect button can land on the
 * wrong rect (the element keeps moving during dispatch).
 */
async function waitForAnimations(el: Element, maxMs = 600): Promise<void> {
  const styles = window.getComputedStyle(el as HTMLElement);
  const tDur = parseDuration(styles.transitionDuration);
  const aDur = parseDuration(styles.animationDuration);
  if (tDur === 0 && aDur === 0) return;

  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', finish);
      el.removeEventListener('animationend', finish);
      clearTimeout(timer);
      resolve();
    };
    el.addEventListener('transitionend', finish, { once: true });
    el.addEventListener('animationend', finish, { once: true });
    const timer = setTimeout(finish, Math.min(maxMs, Math.max(tDur, aDur) + 50));
  });
}

function parseDuration(spec: string): number {
  // CSS may report comma-separated lists for multi-property transitions.
  const first = spec.split(',')[0]?.trim() ?? '0s';
  if (first.endsWith('ms')) return Number(first.slice(0, -2)) || 0;
  if (first.endsWith('s')) return (Number(first.slice(0, -1)) || 0) * 1000;
  return 0;
}

/**
 * Wait for the element's bounding rect to stop moving. After scrollIntoView,
 * lazy-loaded images and async layout can keep nudging the rect for a few
 * hundred ms. Reading rect, clicking, then having React re-render moves the
 * element out from under the dispatch. Re-read every 80ms; resolve when two
 * consecutive reads match within 1px.
 */
async function waitForLayoutStable(el: Element, maxMs = 400): Promise<void> {
  const start = performance.now();
  let last = (el as HTMLElement).getBoundingClientRect();
  while (performance.now() - start < maxMs) {
    await new Promise((r) => setTimeout(r, 80));
    const next = (el as HTMLElement).getBoundingClientRect();
    if (
      Math.abs(next.top - last.top) < 1 &&
      Math.abs(next.left - last.left) < 1 &&
      Math.abs(next.width - last.width) < 1 &&
      Math.abs(next.height - last.height) < 1
    ) {
      return;
    }
    last = next;
  }
}

/** Scroll element into view if not already visible, then wait for it to settle. */
async function ensureVisible(el: Element): Promise<void> {
  const rect = el.getBoundingClientRect();
  const inView =
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth;

  if (!inView) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Replace the fixed 400ms sleep with two real signals: (1) any in-flight
    // CSS animations on the target finish, (2) the element's rect stops
    // moving. Combined budget is bounded; in the common case this is faster
    // than the old fixed sleep.
    await waitForAnimations(el, 600);
    await waitForLayoutStable(el, 400);
  }
}

/** Wait for an element to exist in the DOM, with timeout. */
export async function waitForElement(selector: string, timeoutMs = 5000): Promise<Element> {
  const existing = document.querySelector(selector);
  if (existing) return existing;

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      observer.disconnect();
      reject(new Error(`waitForElement timed out: ${selector}`));
    }, timeoutMs);

    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearTimeout(timeout);
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
}

/**
 * Selectors that match a rendered dropdown option across the libraries we
 * see in the wild. Kept at module scope and exported so the element mapper
 * can use the same list when capturing already-open dropdowns — keeps the
 * executor and the observation layer from drifting on what counts as an
 * "option".
 *
 * Order is informational only; matching is union, not first-hit. Library
 * patterns are listed before generic class globs so that a library's
 * canonical selector wins when both apply.
 */
export const DROPDOWN_OPTION_SELECTORS: readonly string[] = [
  // ARIA-native (Radix Select, native combobox, React-Aria)
  '[role="option"]',
  '[role="listbox"] > *',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
  // Library-specific data attributes
  '[data-radix-collection-item]',
  '[data-combobox-option]',
  '[data-headlessui-state]',
  // Library-specific id prefixes (Headless UI mounts in a portal with these)
  '[id^="headlessui-listbox-option"]',
  '[id^="headlessui-combobox-option"]',
  // Library-specific classes
  '.ant-select-item-option',
  '.MuiMenuItem-root',
  // Generic fallbacks (kept last; broad on purpose)
  'li[data-value]',
  '[class*="select-option"]',
  '[class*="SelectOption"]',
  '[class*="dropdown-item"]',
  '[class*="DropdownItem"]',
  '[class*="ComboboxItem"]',
  '[class*="option"]',
  '[class*="menu-item"]',
  '[class*="MenuItem"]',
];

/**
 * Scrape the labels of every visible dropdown option currently in the DOM,
 * scoped (in priority order) to:
 *   1. The trigger's `aria-controls`/`aria-owns` container, if it resolves.
 *   2. The most recently appeared `[role="listbox"]` / `[role="menu"]` /
 *      `[role="dialog"]` ancestor (last-in-DOM as a proxy for most-recent).
 *   3. Document-wide as a last resort.
 *
 * Used by {@link selectOption} to surface the real available options inside
 * the thrown error when the LLM's guess didn't match — gives the agent a
 * concrete list to retry with instead of guessing again.
 */
function scrapeVisibleOptions(trigger?: Element): string[] {
  const containers: Element[] = [];

  if (trigger) {
    const id = trigger.getAttribute('aria-controls') || trigger.getAttribute('aria-owns');
    if (id) {
      const owned = document.getElementById(id);
      if (owned) containers.push(owned);
    }
  }

  const popups = document.querySelectorAll('[role="listbox"], [role="menu"], [role="dialog"]');
  if (popups.length > 0) containers.push(popups[popups.length - 1]);

  const seen = new Set<string>();
  const labels: string[] = [];
  const collect = (root: ParentNode): void => {
    for (const sel of DROPDOWN_OPTION_SELECTORS) {
      for (const opt of Array.from(root.querySelectorAll(sel))) {
        if ((opt as HTMLElement).getAttribute('aria-disabled') === 'true') continue;
        if ((opt as HTMLElement).classList.contains('disabled')) continue;
        const label = ((opt as HTMLElement).innerText ?? opt.textContent ?? '').trim();
        if (!label || seen.has(label)) continue;
        seen.add(label);
        labels.push(label);
        if (labels.length >= 30) return;
      }
      if (labels.length >= 30) return;
    }
  };

  for (const c of containers) {
    collect(c);
    if (labels.length >= 30) break;
  }
  if (labels.length === 0) collect(document);

  return labels;
}

/**
 * Wait until at least one of the given selectors matches in the DOM. Resolves
 * to true when an element appears (also resolves true if one already exists).
 * Resolves to false on timeout — caller decides what to do. Used by
 * {@link selectOption} to wait for portal-rendered listboxes that may take
 * longer than a fixed setTimeout to mount.
 */
async function waitForAnySelector(selectors: string[], timeoutMs: number): Promise<boolean> {
  if (selectors.some((s) => document.querySelector(s))) return true;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (val: boolean): void => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(val);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    const observer = new MutationObserver(() => {
      if (selectors.some((s) => document.querySelector(s))) finish(true);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
}

/**
 * Dispatch a full user-pointer sequence on an element. Synthetic `.click()`
 * alone only fires the `click` event, but most popular React dropdown
 * libraries (Radix, Headless UI, MUI) bind their open/select handlers to
 * `pointerdown` / `mousedown`. Without these, a Radix `<Select.Trigger>`
 * appears to open (focus + click bubbles) but its state machine is in a
 * half-opened state — and a subsequent `.click()` on an option counts as
 * outside-on-document, dismissing the menu without committing.
 *
 * `composed: true` is required for portal-rendered listboxes that escape
 * the trigger's shadow boundary.
 */
function dispatchPointerSequence(el: Element): void {
  const opts = { bubbles: true, cancelable: true, composed: true } as const;
  try {
    el.dispatchEvent(new PointerEvent('pointerdown', opts));
  } catch {
    // PointerEvent missing in some headless environments; mousedown alone is
    // a usable fallback for Headless UI / MUI (Radix specifically wants
    // pointer events but we degrade gracefully rather than throwing).
  }
  el.dispatchEvent(new MouseEvent('mousedown', opts));
  try {
    el.dispatchEvent(new PointerEvent('pointerup', opts));
  } catch {
    /* see above */
  }
  el.dispatchEvent(new MouseEvent('mouseup', opts));
  (el as HTMLElement).click();
}

/**
 * Internal helper used by every action that targets an element. Calls
 * {@link resolveElement} with the supplied fallback hints; on failure,
 * throws with a message that includes the action name so logs are
 * obvious. The caller surfaces `result.recoveredSelector` back to the
 * platform so the PDP author can see which selector drifted.
 */
function findOrThrow(
  action: string,
  selector: string,
  fallback: SelectorFallback | undefined,
): ResolvedElement {
  const resolved = resolveElement(selector, fallback);
  if (!resolved) {
    const fbDesc = fallback?.label
      ? ` (fallback label "${fallback.label}" also missed)`
      : '';
    throw new Error(`${action} target not found: ${selector}${fbDesc}`);
  }
  return resolved;
}

// ─── Click ─────────────────────────────────────────────────────────────────

/**
 * Click an element with cursor animation.
 * Scrolls into view first if needed.
 *
 * Returns the recovered selector when the primary selector missed but a
 * fallback strategy hit — the caller posts this back to the platform via
 * the iframe-agent reply payload so PDP auto-correct (#42) can suggest a
 * fix to the author.
 */
export async function clickElement(
  selector: string,
  fallback?: SelectorFallback,
): Promise<{ recoveredSelector?: string; verification: VerificationResult }> {
  const t0 = performance.now();
  const resolved = findOrThrow('Click', selector, fallback);
  const el = resolved.element;
  // When fallback fired, animate the cursor to the recovered selector so
  // the visitor sees the right blob of pixels light up.
  const cursorSelector = resolved.recoveredSelector ?? selector;

  // Neutralize target="_blank" to prevent new tabs — navigate in-place instead
  const anchor = el.closest('a') as HTMLAnchorElement | null;
  let savedTarget: string | null = null;
  if (anchor && anchor.target === '_blank') {
    savedTarget = anchor.target;
    anchor.target = '_self';
  }

  await ensureVisible(el);
  await animateCursorTo(cursorSelector);
  await showClickRipple();

  // Stale-node guard: between the initial findOrThrow and dispatch the page
  // may have re-rendered (React reconciliation, lazy hydration, modal
  // animation completing). If `el` is no longer connected to the document,
  // resolve again to a live node before dispatching — otherwise the click
  // fires into a detached subtree and silently misses.
  let target: Element = el;
  if (!target.isConnected) {
    const fresh = resolveElement(selector, fallback);
    if (fresh) target = fresh.element;
  }

  const collectObserved = snapshotPreActionState(target);

  const rect = (target as HTMLElement).getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;

  // Check what's actually at the click point. If it's a sticky header,
  // tooltip, or modal overlay covering the target, the click event will
  // bubble to that overlay's handler, not the target's. We still dispatch
  // (the click might bubble back through the target if it was an
  // ancestor) but mark the verification so the agent sees the mismatch
  // in LAST ACTIONS and can re-plan instead of narrating a wrong-target
  // click as success.
  let clickPointMismatch = false;
  let clickPointActualTag: string | undefined;
  try {
    const doc = (target as HTMLElement).ownerDocument ?? document;
    const atPoint = doc.elementFromPoint(x, y);
    if (atPoint && atPoint !== target && !target.contains(atPoint) && !atPoint.contains(target)) {
      clickPointMismatch = true;
      clickPointActualTag = (atPoint as Element).tagName?.toLowerCase();
    }
  } catch {
    // elementFromPoint can throw on detached docs / cross-origin frames; never block dispatch on it.
  }

  const opts: MouseEventInit = { bubbles: true, cancelable: true, clientX: x, clientY: y, button: 0 };
  target.dispatchEvent(new MouseEvent('mousedown', opts));
  target.dispatchEvent(new MouseEvent('mouseup', opts));
  target.dispatchEvent(new MouseEvent('click', opts));

  if (target instanceof HTMLElement) target.focus();

  // Restore original target after the click's default action has been processed
  if (anchor && savedTarget !== null) {
    requestAnimationFrame(() => { anchor.target = savedTarget!; });
  }

  // D31 — adaptive settle. Clicks can trigger React 18 Suspense fences,
  // framer-motion modal entrances, route-loader cascades — all of which
  // settle past the fixed 250ms. Wait for DOM quiet (≤ 1.5s) so the
  // observation captures the real post-click state, not a half-rendered
  // snapshot that the agent then re-fires against.
  await waitForDomQuiet(120, 1500);
  const observed = collectObserved();
  if (clickPointMismatch) {
    observed.clickPointMismatch = true;
    if (clickPointActualTag) observed.clickPointActualTag = clickPointActualTag;
  }

  // A click is "verified" if anything plausibly downstream of it happened:
  // DOM mutated, route changed, or focus moved. Pure no-op clicks on a div
  // with no handler land in 'executed_unverified' so the agent sees the
  // miss in LAST ACTIONS and re-plans rather than narrating success.
  // When the click point was blocked AND nothing else moved, surface as
  // failed with a specific error so recovery prefers find_alternative_path
  // over re-clicking.
  const hadEffect = observed.domMutated || observed.routeChanged || observed.activeElementChanged;
  let status: VerificationResult['status'];
  let errors: VerificationResult['errors'];
  if (hadEffect) {
    status = 'verified';
  } else if (clickPointMismatch) {
    status = 'failed';
    errors = [{
      code: 'click_point_blocked',
      message: `click point covered by <${clickPointActualTag ?? 'unknown'}> — likely overlay`,
    }];
  } else {
    status = 'executed_unverified';
  }
  const verification: VerificationResult = {
    status,
    observed,
    latencyMs: Math.round(performance.now() - t0),
    resolverStrategy: resolved.strategy,
    ...(errors ? { errors } : {}),
  };
  return { recoveredSelector: resolved.recoveredSelector, verification };
}

// ─── Hover ─────────────────────────────────────────────────────────────────

/**
 * Hover over an element to trigger hover menus/tooltips.
 */
export async function hoverElement(selector: string, durationMs = 1000): Promise<void> {
  const el = document.querySelector(selector);
  if (!el) throw new Error(`Hover target not found: ${selector}`);

  await ensureVisible(el);
  await animateCursorTo(selector);

  const rect = (el as HTMLElement).getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;

  const opts: MouseEventInit = { bubbles: true, cancelable: true, clientX: x, clientY: y };
  el.dispatchEvent(new MouseEvent('mouseenter', opts));
  el.dispatchEvent(new MouseEvent('mouseover', opts));

  await new Promise((r) => setTimeout(r, durationMs));

  el.dispatchEvent(new MouseEvent('mouseleave', opts));
  el.dispatchEvent(new MouseEvent('mouseout', opts));
}

// ─── Keypress ──────────────────────────────────────────────────────────────

/**
 * Parse a chord spec like `"Ctrl+S"`, `"Cmd+K"`, `"Shift+Tab"` into
 * `{ key, modifiers }`. Single keys ("Enter", "Escape", "a") return with
 * no modifiers. Mac-style "Cmd" maps to metaKey; on Windows/Linux models
 * it'll behave the same since most apps listen for either ctrlKey OR
 * metaKey. P1.
 */
function parseChord(spec: string): {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
} {
  const parts = spec.split('+').map((p) => p.trim()).filter(Boolean);
  let ctrlKey = false, metaKey = false, shiftKey = false, altKey = false;
  let key = parts[parts.length - 1] ?? spec;
  for (const p of parts.slice(0, -1)) {
    const lower = p.toLowerCase();
    if (lower === 'ctrl' || lower === 'control') ctrlKey = true;
    else if (lower === 'cmd' || lower === 'meta' || lower === 'super') metaKey = true;
    else if (lower === 'shift') shiftKey = true;
    else if (lower === 'alt' || lower === 'option') altKey = true;
  }
  return { key, ctrlKey, metaKey, shiftKey, altKey };
}

/**
 * Dispatch a keyboard event. Accepts chord notation: "Ctrl+S", "Cmd+K",
 * "Shift+Tab", "Alt+Enter", or single keys: "Enter", "Escape", "a". P1.
 */
export async function pressKey(key: string, target?: string): Promise<{ verification: VerificationResult }> {
  const t0 = performance.now();
  const el = target ? querySelectorDeep(target) : document.activeElement ?? document.body;
  if (!el) throw new Error(`Key target not found: ${target}`);

  const collectObserved = snapshotPreActionState(el);

  const chord = parseChord(key);
  const opts: KeyboardEventInit = {
    key: chord.key,
    code: chord.key.length === 1 ? `Key${chord.key.toUpperCase()}` : chord.key,
    bubbles: true,
    cancelable: true,
    ctrlKey: chord.ctrlKey,
    metaKey: chord.metaKey,
    shiftKey: chord.shiftKey,
    altKey: chord.altKey,
  };

  el.dispatchEvent(new KeyboardEvent('keydown', opts));
  el.dispatchEvent(new KeyboardEvent('keypress', opts));
  el.dispatchEvent(new KeyboardEvent('keyup', opts));

  await new Promise((r) => setTimeout(r, VERIFY_SETTLE_MS));
  const observed = collectObserved();
  const hadEffect = observed.domMutated || observed.routeChanged || observed.activeElementChanged;
  return {
    verification: {
      status: hadEffect ? 'verified' : 'executed_unverified',
      observed,
      latencyMs: Math.round(performance.now() - t0),
      resolverStrategy: 'direct',
    },
  };
}

// ─── File Upload (P1) ──────────────────────────────────────────────────────

/**
 * Programmatically populate an `<input type="file">`. Browsers forbid
 * setting `.files` directly because of the security model around file
 * pickers, but a `DataTransfer` + `change` event is the canonical
 * automation-test workaround. Same-origin only — cross-origin iframes
 * will reject the assignment.
 *
 * `fileSpec` may be:
 *  - a `File`/`Blob` instance
 *  - `{ name, type, content }` for inline synthetic files
 *  - the string id of a baked sample (`'sample.csv'`, `'sample.png'`,
 *    `'sample.pdf'`) — produced inline from a tiny base64 fixture so the
 *    widget bundle doesn't ship megabytes of test data.
 *
 * Does NOT actually upload to the server — the change event is what the
 * site's onChange handler reacts to. The agent narrates and the visitor
 * sees the file selected; submission is whatever the customer's app does
 * next when its onChange handler runs.
 */
export interface UploadFileSpec {
  name: string;
  type: string;
  content?: string;       // utf-8 string content
  base64?: string;        // base64-encoded binary
}

const SAMPLE_FILE_FIXTURES: Record<string, UploadFileSpec> = {
  'sample.csv': {
    name: 'sample.csv',
    type: 'text/csv',
    content: 'name,email,company\nAlex Carter,alex@acme.example,Acme\nJordan Lee,jlee@beacon.example,Beacon',
  },
  'sample.txt': {
    name: 'sample.txt',
    type: 'text/plain',
    content: 'A short demo document used for upload showcases.',
  },
  // 1×1 transparent PNG — small but valid; lets file-preview UIs render.
  'sample.png': {
    name: 'sample.png',
    type: 'image/png',
    base64: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=',
  },
  // Minimal valid PDF — just an empty document, but parsers accept it.
  'sample.pdf': {
    name: 'sample.pdf',
    type: 'application/pdf',
    base64:
      'JVBERi0xLjQKJeLjz9MKMSAwIG9iago8PC9UeXBlL0NhdGFsb2cvUGFnZXMgMiAwIFI+PgplbmRvYmoKMiAwIG9iago8PC9UeXBlL1BhZ2VzL0NvdW50IDEvS2lkc1szIDAgUl0+PgplbmRvYmoKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDIgMCBSL01lZGlhQm94WzAgMCA2MTIgNzkyXT4+CmVuZG9iagp4cmVmCjAgNAowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMTUgMDAwMDAgbiAKMDAwMDAwMDA2MyAwMDAwMCBuIAowMDAwMDAwMTE0IDAwMDAwIG4gCnRyYWlsZXIKPDwvU2l6ZSA0L1Jvb3QgMSAwIFI+PgpzdGFydHhyZWYKMTcwCiUlRU9G',
  },
};

function buildFile(spec: UploadFileSpec | string | File | Blob): File {
  if (spec instanceof File) return spec;
  if (spec instanceof Blob) return new File([spec], 'upload.bin', { type: spec.type || 'application/octet-stream' });
  const resolved = typeof spec === 'string'
    ? (SAMPLE_FILE_FIXTURES[spec] ?? { name: spec, type: 'application/octet-stream', content: '' })
    : spec;
  if (resolved.base64) {
    const binary = Uint8Array.from(atob(resolved.base64), (c) => c.charCodeAt(0));
    return new File([binary], resolved.name, { type: resolved.type });
  }
  return new File([resolved.content ?? ''], resolved.name, { type: resolved.type });
}

export async function uploadFile(
  selector: string,
  fileSpec: UploadFileSpec | string,
  fallback?: SelectorFallback,
): Promise<{ recoveredSelector?: string; verification: VerificationResult }> {
  const t0 = performance.now();
  const resolved = findOrThrow('Upload', selector, fallback);
  const el = resolved.element;
  if (!(el instanceof HTMLInputElement) || el.type !== 'file') {
    throw new Error(`Upload target is not <input type="file">: ${selector}`);
  }
  const collectObserved = snapshotPreActionState(el);
  const file = buildFile(fileSpec);

  // DataTransfer + Object.defineProperty is the canonical Cypress/Playwright
  // recipe — `el.files = ...` is a no-op in modern browsers because the
  // setter is non-enumerable + non-writable for security.
  const dt = new DataTransfer();
  dt.items.add(file);
  Object.defineProperty(el, 'files', { value: dt.files, configurable: true });

  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));

  await new Promise((r) => setTimeout(r, VERIFY_SETTLE_MS));
  const observed = collectObserved();
  const filesNow = el.files;
  const verified = filesNow && filesNow.length > 0 && filesNow[0].name === file.name;
  return {
    recoveredSelector: resolved.recoveredSelector,
    verification: {
      status: verified ? 'verified' : 'failed',
      observed: { ...observed, valueAfter: filesNow?.[0]?.name, valueExpected: file.name },
      errors: verified ? undefined : [{
        code: 'value_mismatch',
        message: `Upload didn't stick — files property reset. Site likely uses a wrapper component that ignores synthetic file injection.`,
      }],
      latencyMs: Math.round(performance.now() - t0),
      resolverStrategy: resolved.strategy,
    },
  };
}

// ─── Drag and drop (P1) ────────────────────────────────────────────────────

/**
 * Drag from one element to another. Dispatches the full pointer +
 * drag/drop event sequence so libraries that listen for either family
 * (HTML5 drag-drop, react-dnd, dnd-kit, hello-pangea/dnd) react. Multi-
 * step pointer move is included so libraries that rely on movement
 * thresholds (e.g. dnd-kit's activation distance) trigger correctly. P1.
 */
export async function drag(
  fromSelector: string,
  toSelector: string,
  fromFallback?: SelectorFallback,
  toFallback?: SelectorFallback,
): Promise<{ verification: VerificationResult }> {
  const t0 = performance.now();
  const fromResolved = findOrThrow('Drag-from', fromSelector, fromFallback);
  const toResolved = findOrThrow('Drag-to', toSelector, toFallback);
  const from = fromResolved.element as HTMLElement;
  const to = toResolved.element as HTMLElement;
  await ensureVisible(from);
  const fromRect = from.getBoundingClientRect();
  const toRect = to.getBoundingClientRect();
  const fromX = fromRect.left + fromRect.width / 2;
  const fromY = fromRect.top + fromRect.height / 2;
  const toX = toRect.left + toRect.width / 2;
  const toY = toRect.top + toRect.height / 2;

  const collectObserved = snapshotPreActionState(from);

  const dt = new DataTransfer();
  const baseOpts = { bubbles: true, cancelable: true, composed: true } as const;

  const dispatch = (el: Element, type: string, x: number, y: number): void => {
    const ev = new DragEvent(type, {
      ...baseOpts,
      clientX: x,
      clientY: y,
      dataTransfer: dt,
    });
    el.dispatchEvent(ev);
  };
  const pointer = (el: Element, type: string, x: number, y: number): void => {
    el.dispatchEvent(new MouseEvent(type, { ...baseOpts, clientX: x, clientY: y, button: 0 }));
    try {
      el.dispatchEvent(new PointerEvent(type, { ...baseOpts, clientX: x, clientY: y, button: 0 }));
    } catch { /* PointerEvent missing in some headless envs */ }
  };

  pointer(from, 'pointerdown', fromX, fromY);
  pointer(from, 'mousedown', fromX, fromY);
  dispatch(from, 'dragstart', fromX, fromY);

  // Multi-step move: dnd-kit / react-beautiful-dnd require movement past
  // an activation threshold; one move event isn't enough.
  const STEPS = 8;
  for (let i = 1; i <= STEPS; i++) {
    const x = fromX + ((toX - fromX) * i) / STEPS;
    const y = fromY + ((toY - fromY) * i) / STEPS;
    pointer(document.elementFromPoint(x, y) ?? to, 'pointermove', x, y);
    pointer(document.elementFromPoint(x, y) ?? to, 'mousemove', x, y);
    dispatch(document.elementFromPoint(x, y) ?? to, 'dragover', x, y);
    await new Promise((r) => setTimeout(r, 30));
  }

  dispatch(to, 'drop', toX, toY);
  dispatch(from, 'dragend', toX, toY);
  pointer(to, 'pointerup', toX, toY);
  pointer(to, 'mouseup', toX, toY);

  await new Promise((r) => setTimeout(r, VERIFY_SETTLE_MS));
  const observed = collectObserved();
  return {
    verification: {
      status: observed.domMutated ? 'verified' : 'executed_unverified',
      observed,
      latencyMs: Math.round(performance.now() - t0),
      resolverStrategy: fromResolved.strategy,
    },
  };
}

// ─── Type Text ─────────────────────────────────────────────────────────────

/**
 * Type text into an input, textarea, or contenteditable element.
 * Handles masked inputs by typing digit-by-digit with pauses.
 *
 * Returns `{ recoveredSelector }` when the primary selector missed and a
 * fallback strategy succeeded.
 */
export async function typeText(
  selector: string,
  text: string,
  speed = 50,
  fallback?: SelectorFallback,
): Promise<{ recoveredSelector?: string; verification: VerificationResult }> {
  const t0 = performance.now();
  const resolved = findOrThrow('Type', selector, fallback);
  const el = resolved.element;
  const cursorSelector = resolved.recoveredSelector ?? selector;

  await ensureVisible(el);
  await animateCursorTo(cursorSelector);

  const collectObserved = snapshotPreActionState(el);

  // Contenteditable support (rich text editors, Notion-like)
  if (el instanceof HTMLElement && el.isContentEditable) {
    await typeIntoContentEditable(el, text, speed);
    await new Promise((r) => setTimeout(r, VERIFY_SETTLE_MS));
    const observed = collectObserved();
    const valueAfter = el.textContent ?? '';
    const verification: VerificationResult = {
      status: valueAfter === text ? 'verified' : 'failed',
      observed: { ...observed, valueAfter, valueExpected: text },
      errors: valueAfter === text ? undefined : [{
        code: 'value_mismatch',
        message: `Expected contenteditable to contain "${text}" but read "${valueAfter}"`,
      }],
      latencyMs: Math.round(performance.now() - t0),
      resolverStrategy: resolved.strategy,
    };
    return { recoveredSelector: resolved.recoveredSelector, verification };
  }

  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
    throw new Error(`Type target is not an input, textarea, or contenteditable: ${selector}`);
  }

  el.focus();
  el.dispatchEvent(new FocusEvent('focus', { bubbles: true }));

  // Detect masked inputs (phone, date, credit card patterns)
  const isMasked = isMaskedInput(el);

  // Clear existing value using native setter (React-compatible)
  setNativeValue(el, '');
  el.dispatchEvent(new Event('input', { bubbles: true }));

  if (isMasked) {
    // Masked inputs: type one character at a time, letting the mask format
    for (const char of text) {
      // Skip formatting characters — only type digits/letters
      if (/[\d\w]/.test(char)) {
        el.dispatchEvent(new KeyboardEvent('keydown', { key: char, bubbles: true }));
        // Insert via InputEvent for better mask compatibility
        el.dispatchEvent(new InputEvent('beforeinput', { inputType: 'insertText', data: char, bubbles: true, cancelable: true }));
        setNativeValue(el, el.value + char);
        el.dispatchEvent(new InputEvent('input', { inputType: 'insertText', data: char, bubbles: true }));
        el.dispatchEvent(new KeyboardEvent('keyup', { key: char, bubbles: true }));
        await new Promise((r) => setTimeout(r, speed + 30));
      }
    }
  } else {
    // Standard inputs: character-by-character with native setter
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      setNativeValue(el, text.substring(0, i + 1));
      el.dispatchEvent(new KeyboardEvent('keydown', { key: char, bubbles: true }));
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new KeyboardEvent('keyup', { key: char, bubbles: true }));
      if (i < text.length - 1) await new Promise((r) => setTimeout(r, speed));
    }
  }

  el.dispatchEvent(new Event('change', { bubbles: true }));

  // D38 — dispatch blur after change so onBlur validators fire and the
  // form's "was-touched" state updates. Without this, libraries like
  // react-hook-form / formik / Vee-validate keep the field pristine,
  // the Save button stays disabled, and the agent narrates "saving"
  // but the click no-ops because submit is gated.
  try {
    el.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
    el.dispatchEvent(new Event('focusout', { bubbles: true }));
  } catch { /* defensive — never fail an action over event dispatch */ }

  await new Promise((r) => setTimeout(r, VERIFY_SETTLE_MS));

  // Read-back verification. Masked inputs are allowed a non-strict match
  // because the mask itself injects formatting chars (e.g. typed "1234567890"
  // ends up as "(123) 456-7890"). For everything else we expect exact match.
  let valueAfter = el.value;
  let status: VerificationResult['status'] = 'verified';
  const errors: VerificationResult['errors'] = [];

  if (isMasked) {
    // Strip non-alphanumeric from both sides before comparing.
    const norm = (s: string) => s.replace(/[^\w\d]/g, '');
    if (norm(valueAfter) !== norm(text)) status = 'failed';
  } else if (valueAfter !== text) {
    // React-controlled inputs sometimes reset the value after our synthetic
    // typing because their state machine never saw a "trusted" event. The
    // canonical recovery is to use the prototype's value setter and a single
    // input event — that's the path React's onChange handler accepts.
    setNativeValue(el, text);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    await new Promise((r) => setTimeout(r, 80));
    valueAfter = el.value;
    if (valueAfter !== text) status = 'failed';
  }

  if (status === 'failed') {
    errors.push({
      code: 'value_mismatch',
      message: `Expected input to contain "${text}" but read "${valueAfter}". ` +
        `Component is likely controlled and rejected synthetic typing.`,
    });
  }

  const observed = collectObserved();
  const verification: VerificationResult = {
    status,
    observed: { ...observed, valueAfter, valueExpected: text },
    errors: errors.length ? errors : undefined,
    latencyMs: Math.round(performance.now() - t0),
    resolverStrategy: resolved.strategy,
  };
  return { recoveredSelector: resolved.recoveredSelector, verification };
}

function isMaskedInput(el: HTMLInputElement | HTMLTextAreaElement): boolean {
  if (el instanceof HTMLTextAreaElement) return false;
  const type = el.getAttribute('type') || '';
  // Phone, date, and credit-card fields are commonly masked
  if (['tel', 'date'].includes(type)) return true;
  // Check for mask libraries (common attribute patterns)
  if (el.getAttribute('data-mask') || el.getAttribute('data-inputmask') || el.getAttribute('mask')) return true;
  // Check if placeholder suggests a mask pattern
  const ph = el.getAttribute('placeholder') || '';
  if (/[_#]\d|[\(\)]\s?\d{3}|MM\/DD|DD\/MM|\d{2}\/\d{2}/.test(ph)) return true;
  return false;
}

async function typeIntoContentEditable(el: HTMLElement, text: string, speed: number): Promise<void> {
  el.focus();
  el.dispatchEvent(new FocusEvent('focus', { bubbles: true }));

  // Clear existing content
  el.textContent = '';
  el.dispatchEvent(new Event('input', { bubbles: true }));

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    el.dispatchEvent(new KeyboardEvent('keydown', { key: char, bubbles: true }));
    el.dispatchEvent(new InputEvent('beforeinput', { inputType: 'insertText', data: char, bubbles: true, cancelable: true }));
    // Insert character at end
    el.textContent = (el.textContent || '') + char;
    // Move cursor to end
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(el);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);

    el.dispatchEvent(new InputEvent('input', { inputType: 'insertText', data: char, bubbles: true }));
    el.dispatchEvent(new KeyboardEvent('keyup', { key: char, bubbles: true }));
    if (i < text.length - 1) await new Promise((r) => setTimeout(r, speed));
  }

  el.dispatchEvent(new Event('change', { bubbles: true }));
}

// ─── Select Option ─────────────────────────────────────────────────────────

/**
 * Select a dropdown option.
 * Handles native <select>, custom dropdowns (Radix, MUI, Headless UI),
 * searchable comboboxes, and virtualized lists.
 */
export async function selectOption(
  selector: string,
  value: string,
  fallback?: SelectorFallback,
): Promise<{ recoveredSelector?: string; verification: VerificationResult }> {
  const t0 = performance.now();
  const resolved = findOrThrow('Select', selector, fallback);
  const el = resolved.element;
  const cursorSelector = resolved.recoveredSelector ?? selector;

  await ensureVisible(el);
  await animateCursorTo(cursorSelector);

  const collectObserved = snapshotPreActionState(el);

  // Native <select>
  if (el instanceof HTMLSelectElement) {
    for (const opt of Array.from(el.options)) {
      if (opt.disabled) continue;
      if (opt.value === value || opt.textContent?.trim() === value) {
        el.value = opt.value;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
        // D31 — adaptive settle so dependent fields (cascading dropdowns,
        // form re-renders triggered by the selection) stabilise before
        // we read state.
        await waitForDomQuiet(120, 1500);
        const observed = collectObserved();
        const valueAfter = el.value;
        const status: VerificationResult['status'] = valueAfter === opt.value ? 'verified' : 'failed';
        return {
          recoveredSelector: resolved.recoveredSelector,
          verification: {
            status,
            observed: { ...observed, valueAfter, valueExpected: opt.value },
            errors: status === 'failed' ? [{
              code: 'value_mismatch',
              message: `Native <select> rejected option "${opt.value}" — value reset to "${valueAfter}". ` +
                `Likely a controlled component that rebinds .value on every render.`,
            }] : undefined,
            latencyMs: Math.round(performance.now() - t0),
            resolverStrategy: resolved.strategy,
          },
        };
      }
    }
    throw new Error(`Option "${value}" not found in native select`);
  }

  // Custom dropdown: open with a real pointer sequence. Synthetic .click()
  // alone leaves Radix/Headless UI triggers in a half-opened state — see
  // dispatchPointerSequence's docstring for the full explanation.
  dispatchPointerSequence(el);

  // Wait for option list to actually render (portal mount can exceed the
  // old fixed 300ms on slow networks / large lists). Cap at 1500ms so a
  // dropdown that genuinely never opens fails fast.
  let opened = await waitForAnySelector(DROPDOWN_OPTION_SELECTORS as string[], 1500);

  // Keyboard fallback: some triggers are bound to keydown rather than pointer
  // events (React-Aria patterns, native <details>-like menus). Try Space →
  // ArrowDown → Enter, with Enter suppressed inside a <form> to avoid
  // accidentally submitting it. 200ms gap between tries; break on first
  // option appearing.
  if (!opened) {
    const insideForm = !!(el as HTMLElement).closest?.('form');
    const keys: string[] = insideForm ? [' ', 'ArrowDown'] : [' ', 'ArrowDown', 'Enter'];
    (el as HTMLElement).focus?.();
    for (const key of keys) {
      const code = key === ' ' ? 'Space' : key;
      const init: KeyboardEventInit = { key, code, bubbles: true, cancelable: true, composed: true };
      el.dispatchEvent(new KeyboardEvent('keydown', init));
      el.dispatchEvent(new KeyboardEvent('keyup', init));
      await new Promise((r) => setTimeout(r, 200));
      if (DROPDOWN_OPTION_SELECTORS.some((s) => document.querySelector(s))) {
        opened = true;
        break;
      }
    }
  }

  if (!opened) {
    const triggerLabel = ((el as HTMLElement).innerText ?? el.textContent ?? '').trim().slice(0, 80);
    throw new Error(
      `Custom dropdown options never appeared after click on ${selector}. ` +
      `Trigger label: "${triggerLabel}". Click the trigger first then re-observe the screen, ` +
      `or check that this element really is a dropdown.`,
    );
  }

  // Check if there's a search input inside the opened dropdown
  const searchInput = findDropdownSearchInput(el);
  if (searchInput) {
    // Type the value to filter, then select the match
    searchInput.focus();
    setNativeValue(searchInput, value);
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    await new Promise((r) => setTimeout(r, 300));
  }

  // The listbox we want to scroll for virtualized lists is the one this
  // trigger controls — not the document's first listbox, which on
  // multi-dropdown screens (filter sidebars) can be a sibling control.
  const ownedId = el.getAttribute('aria-controls') || el.getAttribute('aria-owns');
  const ownedListbox = ownedId ? document.getElementById(ownedId) : null;
  const scrollableListbox =
    (ownedListbox && (ownedListbox.matches('[role="listbox"], [role="menu"]')
      ? ownedListbox
      : ownedListbox.querySelector('[role="listbox"], [role="menu"]'))) ||
    // Fallback: the most recently appeared listbox/menu in the document.
    (() => {
      const all = document.querySelectorAll('[role="listbox"], [role="menu"]');
      return all.length > 0 ? (all[all.length - 1] as HTMLElement) : null;
    })();

  // Try multiple times to handle virtualized lists that render on scroll
  for (let attempt = 0; attempt < 3; attempt++) {
    const allOptions: HTMLElement[] = [];
    const scope: ParentNode = ownedListbox ?? document;
    for (const optSel of DROPDOWN_OPTION_SELECTORS) {
      for (const opt of Array.from(scope.querySelectorAll(optSel))) {
        if ((opt as HTMLElement).getAttribute('aria-disabled') === 'true') continue;
        if ((opt as HTMLElement).classList.contains('disabled')) continue;
        allOptions.push(opt as HTMLElement);
      }
    }
    // If scoping found nothing (rare — e.g. Radix portals outside any
    // aria-controls anchor), retry document-wide rather than failing.
    if (allOptions.length === 0 && scope !== document) {
      for (const optSel of DROPDOWN_OPTION_SELECTORS) {
        for (const opt of Array.from(document.querySelectorAll(optSel))) {
          if ((opt as HTMLElement).getAttribute('aria-disabled') === 'true') continue;
          if ((opt as HTMLElement).classList.contains('disabled')) continue;
          allOptions.push(opt as HTMLElement);
        }
      }
    }
    const match = pickDropdownOption(allOptions, value);
    if (match) {
      await ensureVisible(match);
      // Same reason as the trigger — Radix option items commit selection on
      // pointerdown, not click. A bare .click() counts as outside-on-document
      // and dismisses the menu without committing the value.
      dispatchPointerSequence(match);
      // D31 — adaptive settle. The dropdown closes + the trigger
      // re-renders; on Radix/Headless UI this can take 200-500ms past
      // the old fixed window. Wait for DOM quiet so the verify reads
      // the committed state.
      await waitForDomQuiet(120, 1500);
      const observed = collectObserved();
      // Verify in priority order:
      //  1. An option carries aria-selected="true" AND its trimmed text
      //     equals the matched option's trimmed text. Strongest signal.
      //  2. The matched option's text equals what we asked for (case-
      //     insensitive trimmed). Common when libraries don't update
      //     aria-selected synchronously.
      //  3. The trigger's text contains the value (case-insensitive). Loose
      //     fallback for triggers that reflect the choice but don't expose
      //     aria-selected.
      const ariaSelectedEl = document.querySelector(
        '[role="option"][aria-selected="true"], [role="menuitemradio"][aria-checked="true"]',
      );
      const ariaText = ariaSelectedEl?.textContent?.trim() ?? '';
      const matchText = (match.innerText ?? match.textContent ?? '').trim();
      const triggerText = (el as HTMLElement).innerText?.trim() ?? '';
      const matchedAria = !!ariaText && ariaText === matchText;
      const matchedOptionExact =
        matchText.toLowerCase() === value.trim().toLowerCase();
      const matchedTrigger = triggerText.toLowerCase().includes(value.toLowerCase());
      const status: VerificationResult['status'] =
        (matchedAria || matchedOptionExact || matchedTrigger) ? 'verified' : 'executed_unverified';
      return {
        recoveredSelector: resolved.recoveredSelector,
        verification: {
          status,
          observed: { ...observed, valueAfter: ariaText || matchText || triggerText, valueExpected: value },
          latencyMs: Math.round(performance.now() - t0),
          resolverStrategy: resolved.strategy,
        },
      };
    }
    // If not found, scroll the listbox down and retry (for virtualized lists)
    if (scrollableListbox && attempt < 2) {
      scrollableListbox.scrollTop += scrollableListbox.clientHeight;
      await new Promise((r) => setTimeout(r, 200));
    }
  }

  // Final fallback: keyboard navigation. Some virtualized comboboxes never
  // render the unselected options into the DOM at all (only the
  // currently-highlighted one), so DOM scraping always misses. ArrowDown
  // through up to 50 options and commit with Enter when the highlighted
  // option's text matches.
  const ariaActiveTarget =
    ownedListbox ??
    (document.querySelector('[role="listbox"], [role="menu"]') as HTMLElement | null);
  if (ariaActiveTarget) {
    const targetLower = value.trim().toLowerCase();
    let lastSeen = '';
    let stableTicks = 0;
    for (let i = 0; i < 50; i++) {
      const init: KeyboardEventInit = {
        key: 'ArrowDown', code: 'ArrowDown', bubbles: true, cancelable: true, composed: true,
      };
      ariaActiveTarget.dispatchEvent(new KeyboardEvent('keydown', init));
      ariaActiveTarget.dispatchEvent(new KeyboardEvent('keyup', init));
      await new Promise((r) => setTimeout(r, 30));
      const active = document.querySelector(
        '[role="option"][aria-selected="true"], [role="option"].active, [role="option"][data-highlighted], ' +
        '[role="menuitem"][data-highlighted], [role="menuitemradio"][data-highlighted]',
      ) as HTMLElement | null;
      const text = (active?.innerText ?? active?.textContent ?? '').trim().toLowerCase();
      if (text && text === targetLower) {
        const enter: KeyboardEventInit = {
          key: 'Enter', code: 'Enter', bubbles: true, cancelable: true, composed: true,
        };
        ariaActiveTarget.dispatchEvent(new KeyboardEvent('keydown', enter));
        ariaActiveTarget.dispatchEvent(new KeyboardEvent('keyup', enter));
        await new Promise((r) => setTimeout(r, VERIFY_SETTLE_MS));
        const observed = collectObserved();
        const triggerText = (el as HTMLElement).innerText?.trim() ?? '';
        return {
          recoveredSelector: resolved.recoveredSelector,
          verification: {
            status: 'verified',
            observed: { ...observed, valueAfter: triggerText, valueExpected: value },
            latencyMs: Math.round(performance.now() - t0),
            resolverStrategy: resolved.strategy,
          },
        };
      }
      // Wrap detection — if active option text repeats for 3 ticks we've
      // looped through the whole list, no need to keep pressing.
      if (text && text === lastSeen) {
        stableTicks++;
        if (stableTicks >= 3) break;
      } else {
        stableTicks = 0;
        lastSeen = text;
      }
    }
  }

  // Still no match. Hand the LLM the actual visible options so its retry can
  // pass a real label instead of guessing. The bare textContent list goes in
  // the message itself because `iframe-agent` only propagates `err.message`
  // back to the server (`sendResult(id, false, message)`).
  const scraped = scrapeVisibleOptions(el);
  const optionsHint = scraped.length > 0
    ? ` Available options: ${JSON.stringify(scraped)}.`
    : ' No options were visible to scrape — the menu may have closed.';
  throw new Error(`Option "${value}" not found in dropdown ${selector}.${optionsHint}`);
}

/**
 * Rank-based option matcher. Tries strategies in order; returns the first
 * unambiguous match:
 *   1. Exact `textContent.trim()` equality.
 *   2. Exact `data-value` equality.
 *   3. Case-insensitive trimmed equality.
 *   4. `textContent.includes()` (case-insensitive) — but only when exactly
 *      one option matches. Required because real options often carry
 *      suffixes/icons ("California (CA)", "🇺🇸 California"), but a naive
 *      `includes` could match "United States" inside both
 *      "United States" and "United States Minor Outlying Islands".
 */
function pickDropdownOption(options: HTMLElement[], value: string): HTMLElement | null {
  const target = value.trim();
  const targetLower = target.toLowerCase();

  for (const opt of options) {
    if (opt.textContent?.trim() === target) return opt;
  }
  for (const opt of options) {
    if (opt.getAttribute('data-value') === target) return opt;
  }
  for (const opt of options) {
    if (opt.textContent?.trim().toLowerCase() === targetLower) return opt;
  }
  const partials = options.filter((opt) =>
    (opt.textContent ?? '').trim().toLowerCase().includes(targetLower),
  );
  if (partials.length === 1) return partials[0];
  return null;
}

/** Find search input inside an opened dropdown (combobox, searchable select) */
function findDropdownSearchInput(trigger: Element): HTMLInputElement | null {
  // Check aria-controls for linked popup
  const controlsId = trigger.getAttribute('aria-controls') || trigger.getAttribute('aria-owns');
  const popup = controlsId ? document.getElementById(controlsId) : null;

  // Look for search input in popup, or in any recently-appeared listbox/popover
  const containers = [
    popup,
    document.querySelector('[role="listbox"]')?.parentElement,
    document.querySelector('[data-radix-popper-content-wrapper]'),
    document.querySelector('[class*="dropdown"][class*="open"]'),
    document.querySelector('[class*="popover"][class*="show"]'),
  ].filter(Boolean) as Element[];

  for (const container of containers) {
    const input = container.querySelector('input[type="text"], input[type="search"], input:not([type])');
    if (input && input !== trigger) return input as HTMLInputElement;
  }

  return null;
}

// ─── Scroll ────────────────────────────────────────────────────────────────

/**
 * Scroll to an element smoothly.
 */
export async function scrollTo(
  selector: string,
  fallback?: SelectorFallback,
): Promise<{ recoveredSelector?: string; verification: VerificationResult }> {
  const t0 = performance.now();
  const resolved = findOrThrow('Scroll', selector, fallback);
  resolved.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  await new Promise((r) => setTimeout(r, 500));
  // After the scroll, the element should be reasonably in-viewport. If it
  // isn't, the page intercepted the scroll (sticky overlay, pinned header,
  // scroll lock) — flag as unverified so the agent can re-plan.
  const rect = (resolved.element as HTMLElement).getBoundingClientRect();
  const inView =
    rect.top >= -10 && rect.bottom <= window.innerHeight + 10 &&
    rect.left >= -10 && rect.right <= window.innerWidth + 10;
  return {
    recoveredSelector: resolved.recoveredSelector,
    verification: {
      status: inView ? 'verified' : 'executed_unverified',
      observed: {},
      latencyMs: Math.round(performance.now() - t0),
      resolverStrategy: resolved.strategy,
    },
  };
}

// ─── Highlight ─────────────────────────────────────────────────────────────

/**
 * Show a spotlight highlight on an element.
 */
/**
 * Visual style for {@link highlightElement}. New styles added in #29:
 *   spotlight (default) — dim the rest of the page; classic SE move.
 *   pulse              — soft breathing ring; gentle "look here".
 *   border             — single outline; least visually loud, good for
 *                        long demos where spotlight gets fatiguing.
 *   arrow              — SVG arrow pointing AT the element from offscreen
 *                        side (auto-picked: nearest viewport edge).
 *   bracket            — corner brackets around a region (e.g. a row of
 *                        cards). Better than spotlight for groups.
 *   circle             — ring around a small element (icon, avatar).
 */
export type HighlightStyle = 'spotlight' | 'pulse' | 'border' | 'arrow' | 'bracket' | 'circle';

export function highlightElement(
  selector: string,
  tooltip?: string,
  durationMs = 3000,
  fallback?: SelectorFallback,
  style: HighlightStyle = 'spotlight',
): () => void {
  // highlightElement is synchronous so it doesn't return recoveredSelector
  // — the iframe-agent dispatcher uses resolveElement directly when it
  // needs to bubble that information back.
  const resolved = resolveElement(selector, fallback);
  if (!resolved) throw new Error(`Highlight target not found: ${selector}`);
  const el = resolved.element;

  injectHighlightStyles();

  // P1 (#89): confidence — when the resolver had to fall back from the
  // primary selector, render in muted style so the visitor sees the
  // agent is best-guessing rather than locked-on.
  const muted = resolved.strategy !== 'direct';

  const rect = el.getBoundingClientRect();
  const cleanups: Array<() => void> = [];

  switch (style) {
    case 'spotlight': {
      cleanups.push(renderSpotlight(rect));
      break;
    }
    case 'pulse': {
      cleanups.push(renderPulse(rect, muted));
      break;
    }
    case 'border': {
      cleanups.push(renderBorder(rect, muted));
      break;
    }
    case 'arrow': {
      cleanups.push(renderArrow(rect));
      break;
    }
    case 'bracket': {
      cleanups.push(renderBracket(rect, muted));
      break;
    }
    case 'circle': {
      cleanups.push(renderCircle(rect, muted));
      break;
    }
    default: {
      // Unknown style — fall back to spotlight so a future server change
      // that adds a style doesn't leave older widgets blank.
      cleanups.push(renderSpotlight(rect));
    }
  }

  if (tooltip) {
    cleanups.push(renderTooltip(rect, tooltip));
  }

  const cleanup = () => {
    for (const fn of cleanups) {
      try { fn(); } catch { /* ignore */ }
    }
  };

  if (durationMs > 0) setTimeout(cleanup, durationMs);
  return cleanup;
}

/**
 * Inject the (idempotent) stylesheet shared by every highlight style. The
 * styles use very high z-indices so they sit above any customer modals.
 */
function injectHighlightStyles(): void {
  if (document.getElementById('da-highlight-styles')) return;
  const style = document.createElement('style');
  style.id = 'da-highlight-styles';
  style.textContent = `
    .da-spotlight-mask { position:fixed; inset:0; z-index:2147483640; background:rgba(0,0,0,0.5); pointer-events:none; transition:clip-path 0.3s ease; }
    .da-tooltip-float { position:fixed; z-index:2147483642; max-width:300px; padding:8px 14px; background:#1e1e2e; color:#e2e8f0; border-radius:8px; font-family:Inter,system-ui,sans-serif; font-size:13px; line-height:1.5; box-shadow:0 4px 20px rgba(0,0,0,0.25); pointer-events:none; }
    .da-pulse-ring { position:fixed; z-index:2147483641; pointer-events:none; border-radius:8px; box-shadow:0 0 0 4px rgba(99,102,241,0.85); animation:daPulseScale 1.4s ease-in-out infinite; }
    @keyframes daPulseScale { 0%,100% { box-shadow:0 0 0 4px rgba(99,102,241,0.85); } 50% { box-shadow:0 0 0 10px rgba(99,102,241,0.0); } }
    .da-border-ring { position:fixed; z-index:2147483641; pointer-events:none; border:2px solid #6366f1; border-radius:6px; }
    .da-circle-ring { position:fixed; z-index:2147483641; pointer-events:none; border:3px solid #6366f1; border-radius:50%; box-shadow:0 0 0 6px rgba(99,102,241,0.18); }
    .da-bracket-corner { position:fixed; z-index:2147483641; pointer-events:none; width:18px; height:18px; border:3px solid #6366f1; }
    .da-arrow-svg { position:fixed; z-index:2147483641; pointer-events:none; }
    /* P1 (#89): muted variant for highlights placed via fallback resolver
       strategy. The agent best-guessed which element to highlight; the
       muted styling tells the visitor "this is approximately the spot". */
    .da-highlight-muted.da-pulse-ring { box-shadow:0 0 0 3px rgba(99,102,241,0.45); animation:daPulseScale 1.8s ease-in-out infinite; }
    .da-highlight-muted.da-border-ring { border:2px dashed rgba(99,102,241,0.65); }
    .da-highlight-muted.da-circle-ring { border:3px dashed rgba(99,102,241,0.55); box-shadow:0 0 0 4px rgba(99,102,241,0.10); }
    .da-highlight-muted.da-bracket-corner { border-color: rgba(99,102,241,0.55); }
    /* P1 (#90): recovery toast. Shown briefly when an action fails to
       verify so the visitor sees that the agent noticed. */
    .da-recovery-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); z-index:2147483643;
      padding:10px 16px; background:#1e1e2e; color:#fde68a; border:1px solid #b45309;
      border-radius:8px; font-family:Inter,system-ui,sans-serif; font-size:13px; line-height:1.4;
      box-shadow:0 8px 24px rgba(0,0,0,0.35); pointer-events:none; opacity:0; animation:daToastIn 240ms ease forwards; }
    @keyframes daToastIn { from { opacity:0; transform:translate(-50%, 8px); } to { opacity:1; transform:translate(-50%, 0); } }
  `;
  document.head.appendChild(style);
}

/**
 * Show a brief on-page recovery toast. Used by the iframe-agent when an
 * action's verification status came back failed/unverified — the visitor
 * hears the agent explain what happened, but a visible cue makes the
 * recovery feel more honest. P1 (#90).
 */
export function showRecoveryToast(message: string, durationMs = 2400): () => void {
  injectHighlightStyles();
  const el = document.createElement('div');
  el.className = 'da-recovery-toast';
  el.textContent = message.slice(0, 120);
  document.body.appendChild(el);
  const t = setTimeout(() => el.remove(), durationMs);
  return () => { clearTimeout(t); el.remove(); };
}

function renderSpotlight(rect: DOMRect): () => void {
  const p = 8;
  const mask = document.createElement('div');
  mask.className = 'da-spotlight-mask';
  mask.style.clipPath = `polygon(0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%, ${rect.left - p}px ${rect.top - p}px, ${rect.right + p}px ${rect.top - p}px, ${rect.right + p}px ${rect.bottom + p}px, ${rect.left - p}px ${rect.bottom + p}px, ${rect.left - p}px ${rect.top - p}px)`;
  document.body.appendChild(mask);
  return () => mask.remove();
}

function renderPulse(rect: DOMRect, muted = false): () => void {
  const ring = document.createElement('div');
  ring.className = `da-pulse-ring${muted ? ' da-highlight-muted' : ''}`;
  ring.style.left = `${rect.left}px`;
  ring.style.top = `${rect.top}px`;
  ring.style.width = `${rect.width}px`;
  ring.style.height = `${rect.height}px`;
  document.body.appendChild(ring);
  return () => ring.remove();
}

function renderBorder(rect: DOMRect, muted = false): () => void {
  const ring = document.createElement('div');
  ring.className = `da-border-ring${muted ? ' da-highlight-muted' : ''}`;
  ring.style.left = `${rect.left - 2}px`;
  ring.style.top = `${rect.top - 2}px`;
  ring.style.width = `${rect.width}px`;
  ring.style.height = `${rect.height}px`;
  document.body.appendChild(ring);
  return () => ring.remove();
}

function renderCircle(rect: DOMRect, muted = false): () => void {
  // Use the longer dimension as the diameter so a non-square element is
  // still cleanly enclosed by the ring.
  const size = Math.max(rect.width, rect.height) + 8;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const ring = document.createElement('div');
  ring.className = `da-circle-ring${muted ? ' da-highlight-muted' : ''}`;
  ring.style.left = `${cx - size / 2}px`;
  ring.style.top = `${cy - size / 2}px`;
  ring.style.width = `${size}px`;
  ring.style.height = `${size}px`;
  document.body.appendChild(ring);
  return () => ring.remove();
}

function renderBracket(rect: DOMRect, muted = false): () => void {
  // Four little L-corners. Each corner is a fixed-size square with two
  // borders set; positioning differs per corner.
  const inset = 6;
  const corners: Array<{ x: number; y: number; sides: [keyof CSSStyleDeclaration, keyof CSSStyleDeclaration] }> = [
    { x: rect.left - inset, y: rect.top - inset, sides: ['borderLeftStyle', 'borderTopStyle'] },
    { x: rect.right + inset - 18, y: rect.top - inset, sides: ['borderRightStyle', 'borderTopStyle'] },
    { x: rect.left - inset, y: rect.bottom + inset - 18, sides: ['borderLeftStyle', 'borderBottomStyle'] },
    { x: rect.right + inset - 18, y: rect.bottom + inset - 18, sides: ['borderRightStyle', 'borderBottomStyle'] },
  ];
  const created: HTMLElement[] = [];
  for (const c of corners) {
    const div = document.createElement('div');
    div.className = `da-bracket-corner${muted ? ' da-highlight-muted' : ''}`;
    div.style.left = `${c.x}px`;
    div.style.top = `${c.y}px`;
    // CSS class sets all four borders solid; clear the two that don't
    // belong to this corner so we get a true L-shape.
    div.style.borderTopStyle = 'none';
    div.style.borderRightStyle = 'none';
    div.style.borderBottomStyle = 'none';
    div.style.borderLeftStyle = 'none';
    for (const side of c.sides) {
      (div.style as unknown as Record<string, string>)[side as string] = 'solid';
    }
    document.body.appendChild(div);
    created.push(div);
  }
  return () => created.forEach((d) => d.remove());
}

function renderArrow(rect: DOMRect): () => void {
  // Pick the nearest viewport edge to anchor the arrow tail. Keeps the
  // arrow visible regardless of where on screen the element is.
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const dists = {
    left: rect.left,
    right: window.innerWidth - rect.right,
    top: rect.top,
    bottom: window.innerHeight - rect.bottom,
  };
  const edge = (Object.entries(dists) as Array<[keyof typeof dists, number]>)
    .sort((a, b) => b[1] - a[1])[0][0]; // farthest edge → arrow comes FROM there

  // Tail point relative to the chosen edge, with a small offset so the
  // arrow head doesn't sit on the element.
  let tailX = cx;
  let tailY = cy;
  const offset = 80;
  if (edge === 'left') tailX = Math.max(20, rect.left - offset);
  else if (edge === 'right') tailX = Math.min(window.innerWidth - 20, rect.right + offset);
  else if (edge === 'top') tailY = Math.max(20, rect.top - offset);
  else if (edge === 'bottom') tailY = Math.min(window.innerHeight - 20, rect.bottom + offset);

  const headX = (() => {
    if (edge === 'left') return rect.left - 8;
    if (edge === 'right') return rect.right + 8;
    return cx;
  })();
  const headY = (() => {
    if (edge === 'top') return rect.top - 8;
    if (edge === 'bottom') return rect.bottom + 8;
    return cy;
  })();

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'da-arrow-svg');
  // Cover the whole viewport so we can use absolute coords without an
  // intermediate transform.
  svg.style.left = '0';
  svg.style.top = '0';
  svg.style.width = `${window.innerWidth}px`;
  svg.style.height = `${window.innerHeight}px`;

  // Define the arrowhead marker once.
  svg.innerHTML = `
    <defs>
      <marker id="da-arrowhead" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#6366f1" />
      </marker>
    </defs>
    <line x1="${tailX}" y1="${tailY}" x2="${headX}" y2="${headY}" stroke="#6366f1" stroke-width="3" marker-end="url(#da-arrowhead)" />
  `;
  document.body.appendChild(svg);
  return () => svg.remove();
}

function renderTooltip(rect: DOMRect, tooltip: string): () => void {
  const tooltipEl = document.createElement('div');
  tooltipEl.className = 'da-tooltip-float';
  tooltipEl.textContent = tooltip;
  // Smart positioning: below if near top, above otherwise
  const above = rect.top > 60;
  tooltipEl.style.left = `${Math.max(8, rect.left)}px`;
  tooltipEl.style.top = above ? `${rect.top - 44}px` : `${rect.bottom + 12}px`;
  document.body.appendChild(tooltipEl);
  return () => tooltipEl.remove();
}
